import { CommonResourceKey } from '@shared/models/ResourceKeys';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import TextButton from '../core/button/TextButton';
import { IconName } from '../core/images/IconMap';

interface IOrgLinksProps {
    variant:
        | 'orgDetails'
        | 'orgBilling'
        | 'orgInvites'
        | 'orgMembers'
        | 'orgMe'
        | 'orgAuthClient'
        | 'orgMetrics'
        | 'orgProjects'
        | 'orgSettings'
        | 'orgBillingRedirect'
        | 'orgTrialBillingRedirect'
        | 'orgLicenseKeys'
        | 'orgManagedOrgs'
        | 'orgProfiles'
        | 'orgProduct';
    organizationId: string;
    textOverride?: string;
}

export default function OrgLinks({ variant, organizationId, textOverride }: IOrgLinksProps) {
    const { t } = useTranslation();
    const { organizationId: parentOrgId, managedOrgId } = useParams();
    let url: string;
    let textKey: CommonResourceKey = 'common:other.title';
    let endIcon: IconName | undefined;
    let appendManagedOrg = false;

    if (!variant || !organizationId) return null;

    // handle for reseller managed org navigation
    if (organizationId !== parentOrgId && parentOrgId && organizationId === managedOrgId) {
        organizationId = parentOrgId;
        appendManagedOrg = true;
    }

    switch (variant) {
        case 'orgMembers':
            url = `/members`;
            textKey = 'common:organization.canViewMembers.title';
            break;
        case 'orgBilling':
            url = `/billing`;
            textKey = 'common:organization.canManageBilling.title';
            break;
        case 'orgDetails':
            url = `/`;
            textKey = 'common:organization.titleLong';
            break;
        case 'orgInvites':
            url = `/members/invites`;
            textKey = 'common:organization.invite.view';
            break;
        case 'orgMe':
            url = `/`;
            textKey = 'common:organization.member.me';
            break;
        case 'orgAuthClient':
            url = `/projects`;
            textKey = 'common:organization.canCreateAuthClients.title';
            break;
        case 'orgMetrics':
            url = `/metrics`;
            textKey = 'common:organization.metrics.view';
            break;
        case 'orgProjects':
            url = `/projects`;
            textKey = 'common:organization.projects.view';
            break;
        case 'orgBillingRedirect':
            url = `/billing/redirect/upgrade`;
            textKey = 'common:billing.view';
            endIcon = 'externalLink';
            break;
        case 'orgTrialBillingRedirect':
            url = `/trial/redirect/upgrade`;
            textKey = 'common:billing.view';
            endIcon = 'externalLink';
            break;
        case 'orgSettings':
            url = `/settings`;
            textKey = 'common:settings.title';
            break;
        case 'orgLicenseKeys':
            url = `/licensekey`;
            textKey = 'common:license.key.titlePlural';
            break;
        case 'orgManagedOrgs':
            url = `/managed`;
            textKey = 'common:organization.managed.title';
            break;
        case 'orgProduct':
            url = `/purchase`;
            textKey = 'common:license.key.product.title';
            break;
        case 'orgProfiles':
            url = `/profiles`;
            textKey = 'common:organization.profiles.title';
            break;
        default:
            return null;
    }

    return (
        <TextButton
            variant="subsection"
            to={
                appendManagedOrg
                    ? `/org/${organizationId}/managed/${managedOrgId}` + url
                    : `/org/${organizationId}` + url
            }
            viewTransition
            endIcon={endIcon}>
            {textOverride ?? t(textKey)}
        </TextButton>
    );
}
